// One list-products request per page load, shared by every caller.
//
// ShopPage and useCategories each fetched the same grid list, so loading the shop made
// two identical ~360 kB requests at ~3s apiece. This memoises the in-flight promise per
// view: whoever asks second awaits the first instead of starting a second round trip.
//
// The cache lives for the page's lifetime and is gone on reload, which is the right
// lifetime for a shop - a shopper who refreshes should see current stock.
import { shopConfig as cfg } from './shopConfig';

const inflight: Record<string, Promise<any[]>> = {};

// Last-known list, kept for the session. The grid list is ~360 kB and the function
// needs ~2.5s to read 259 documents, and that call sits on the critical path for LCP -
// the first product image cannot even begin loading until the JSON lands. So a repeat
// visit or a navigation back to the shop renders from this immediately and refreshes
// behind the shopper. Stock can therefore be a few minutes stale on screen; the cart
// and checkout always re-read it server-side, so nothing can be bought on stale data.
const SS_KEY = 'dbos_products_';

function readCache(view: string): any[] | null {
  try {
    const raw = sessionStorage.getItem(SS_KEY + view);
    if (!raw) return null;
    const { at, products } = JSON.parse(raw);
    if (!Array.isArray(products) || Date.now() - at > 10 * 60 * 1000) return null;
    return products;
  } catch { return null; }
}

function writeCache(view: string, products: any[]) {
  try {
    sessionStorage.setItem(SS_KEY + view, JSON.stringify({ at: Date.now(), products }));
  } catch { /* private mode or quota - the fetch path still works */ }
}

export function cachedProducts(view: 'grid' | 'full' = 'grid'): any[] | null {
  return readCache(view);
}

export function fetchProducts(view: 'grid' | 'full' = 'grid'): Promise<any[]> {
  if (!inflight[view]) {
    const url = `${cfg.api_base}/smartshop-list-products?gido_id=${cfg.gido_id}`
      + (view === 'grid' ? '&view=grid' : '');
    inflight[view] = fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((data) => {
        const products = data.products || [];
        writeCache(view, products);
        return products;
      })
      .catch((err) => {
        // never cache a failure - the next caller must be able to retry
        delete inflight[view];
        throw err;
      });
  }
  return inflight[view];
}